/**
 * TimelineItem Component
 *
 * Displays a single activity in the vertical timeline with type-specific icon,
 * title, date, and optional description.
 *
 * Requirements: Task 8 - Activity Timeline Page
 */

import { Badge } from '@bloomcode/ui';
import {
  Mail,
  Phone,
  Calendar,
  FileText,
  CheckCircle,
  MessageSquare,
  Activity,
} from '@bloomcode/icons';
import type { GetMyActivitiesQuery } from '@bloomcode/graphql';

type ActivityItem = GetMyActivitiesQuery['getMyActivities']['data'][number];

interface TimelineItemProps {
  activity: ActivityItem;
  isLast: boolean;
}

/**
 * Configuration for each activity type — icon, color, and label
 */
const activityTypeConfig: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>;
    colorClass: string;
    label: string;
  }
> = {
  EMAIL: {
    icon: Mail,
    colorClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    label: 'Email',
  },
  CALL: {
    icon: Phone,
    colorClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    label: 'Call',
  },
  MEETING: {
    icon: Calendar,
    colorClass: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    label: 'Meeting',
  },
  NOTE: {
    icon: FileText,
    colorClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    label: 'Note',
  },
  TASK: {
    icon: CheckCircle,
    colorClass: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
    label: 'Task',
  },
  TICKET: {
    icon: MessageSquare,
    colorClass: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    label: 'Ticket',
  },
};

const defaultConfig = {
  icon: Activity,
  colorClass: 'bg-muted text-muted-foreground',
  label: 'Activity',
};

/**
 * Formats a date string to a readable format with time
 */
function formatActivityDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Returns a relative time string for recent activities
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatActivityDate(dateString);
}

export function TimelineItem({ activity, isLast }: TimelineItemProps) {
  const config = activityTypeConfig[activity.type] ?? defaultConfig;
  const Icon = config.icon;

  return (
    <div className="flex gap-3" role="listitem">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${config.colorClass}`}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" />
        </div>
        {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
      </div>

      {/* Content */}
      <div className={`min-w-0 flex-1 ${isLast ? 'pb-0' : 'pb-6'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{activity.title}</p>
            {activity.description && (
              <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
                {activity.description}
              </p>
            )}
          </div>
          {activity.isCompleted && (
            <Badge variant="outline" className="shrink-0 text-xs">
              Completed
            </Badge>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">{config.label}</span>
          <span aria-hidden="true">·</span>
          <time dateTime={activity.date}>{formatRelativeTime(activity.date)}</time>
          {activity.duration && activity.duration > 0 && (
            <>
              <span aria-hidden="true">·</span>
              <span>{activity.duration} min</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { activityTypeConfig, defaultConfig };
export type { TimelineItemProps, ActivityItem };
export default TimelineItem;
